import { isoDay, type Counts, type Folder, type InstructionBody, type MailAction, type MailActionResult, type MailDetail, type MailListItem, type MailListQuery, type MailListResponse, type MailSort, type MailView, type ProcessedFilter, type Task, type TaskSummary, type WsEvent } from '@jerome2/shared';
import { api, qs } from '../api.ts';
import { createStore } from '../lib/store.ts';
import { app } from './app.ts';
import { toast } from './toast.ts';
import { HIDING_STATUSES, latestTaskForMessage, loadCounts, READY_STATUSES, tasksStore, upsertTask } from './tasks.ts';

// Mail: mappen, lijsten per filter/map/sortering/zoekterm, lezerdetails met cache en vooruit
// ophalen, directe acties met ongedaan maken (lijsten en tellers werken direct mee), doorzetten
// naar de einde-dag-samenvatting, en instructies naar de wachtrij. Bij Verwerkt is elk item een
// taak (een mail met meerdere acties staat er meerdere keren); de selectie is daar per taak.
// Eigen acties worden kort onthouden: het seintje van de server daarover (mail.changed) hoeft
// niets te doen, en een lijstantwoord dat de actie nog niet kent draait haar niet terug.

/** Selectie in een lijst: de mail, en bij Verwerkt ook de taak. */
export interface Sel {
  id: string;
  taskId?: string | null | undefined;
}

/** Sleutel van een lijstitem: bij Verwerkt mail plus taak, elders alleen de mail. */
export function itemKey(item: MailListItem, view: MailView): string {
  return view === 'processed' && item.task ? `${item.id}#${item.task.id}` : item.id;
}

/**
 * Past dit item bij de selectie? Een selectie met taak vergelijkt op de taak (Verwerkt, en de items onder "Klaar voor
 * jou"); een selectie zonder taak is de mail zelf en past niet op een rij die op Jeroen wacht (die heeft altijd een taak).
 */
export function matchesSel(item: MailListItem, sel: Sel | string | null, _view?: MailView): boolean {
  if (!sel) return false;
  const s: Sel = typeof sel === 'string' ? { id: sel } : sel;
  if (item.id !== s.id) return false;
  if (!s.taskId) return !isReadyItem(item);
  if (!item.task) return true;
  return item.task.id === s.taskId;
}

/** Hoort bij dit item een taak die op Jeroen wacht (resultaat of vraag)? Dan toont de lezer de resultaatkaart, ook buiten Verwerkt. */
export function isReadyItem(item: Pick<MailListItem, 'task'>): boolean {
  return !!item.task && READY_STATUSES.has(item.task.status);
}

/** De taken die klaar zijn of een vraag hebben, als lijstitems (nieuwste eerst), voor het blok "Klaar voor jou" boven Gevlagd en Ongelezen. */
export function readyItems(byId: Record<string, Task>): MailListItem[] {
  return Object.values(byId)
    .filter((t) => READY_STATUSES.has(t.status))
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .map((t) => itemFromTask(t, summaryOf(t)));
}

export interface ListKey {
  view: MailView;
  folder: string;
  sort: MailSort;
  processed: ProcessedFilter;
  q: string;
}

export interface ListState {
  items: MailListItem[];
  more: boolean;
  loading: boolean;
  error: string | null;
  loadedAt: number;
  stale: boolean;
}

export interface MailState {
  folders: Folder[];
  foldersLoadedAt: number;
  lists: Record<string, ListState>;
  details: Record<string, { detail: MailDetail; at: number }>;
  detailErrors: Record<string, string>;
}

export const mailStore = createStore<MailState>({ folders: [], foldersLoadedAt: 0, lists: {}, details: {}, detailErrors: {} });

export const PAGE = 25; // kleinere pagina: de connector heeft bij 50 ongelezen mails ~18 s nodig, bij 25 een paar seconden

export function listKey(k: ListKey): string {
  return [k.view, k.folder, k.sort, k.processed, k.q.trim().toLowerCase()].join('|');
}

export function emptyList(): ListState {
  return { items: [], more: false, loading: false, error: null, loadedAt: 0, stale: false };
}

function setList(key: string, patch: Partial<ListState>): void {
  mailStore.set((s) => ({ ...s, lists: { ...s.lists, [key]: { ...(s.lists[key] ?? emptyList()), ...patch } } }));
}

/** Eigen acties van zojuist: zo lang negeert de app het seintje van de server erover (OWN_EVENT_MS) en laat een lijstantwoord ze staan (OWN_LIST_MS, de connector loopt soms even achter). */
const OWN_EVENT_MS = 5_000;
const OWN_LIST_MS = 10_000;
const ownActions = new Map<string, { action: MailAction; at: number }>();

function noteOwn(id: string, action: MailAction): void {
  ownActions.set(id, { action, at: Date.now() });
  if (ownActions.size > 100) for (const [k, v] of ownActions) if (Date.now() - v.at > OWN_LIST_MS) ownActions.delete(k);
}

function ownRecent(id: string, ms: number): MailAction | null {
  const o = ownActions.get(id);
  return o && Date.now() - o.at < ms ? o.action : null;
}

/** Voor tests: de eigen acties vergeten. */
export function forgetOwnActions(): void {
  ownActions.clear();
}

/** De mails die open staan (de lezer): bij een seintje van de server over zo'n mail wordt het detail stil ververst in plaats van gewist. */
const watched = new Map<string, number>();
export function watchDetail(id: string): () => void {
  watched.set(id, (watched.get(id) ?? 0) + 1);
  return () => {
    const n = (watched.get(id) ?? 1) - 1;
    if (n <= 0) watched.delete(id);
    else watched.set(id, n);
  };
}

/** `fresh`: langs de korte cache van de server heen, na een verandering die de app zelf kent (verzonden concept, actie). */
export async function loadFolders(force = false, opts: { background?: boolean; fresh?: boolean } = {}): Promise<Folder[]> {
  const s = mailStore.get();
  if (!force && s.folders.length && Date.now() - s.foldersLoadedAt < 60_000) return s.folders;
  const r = await api<{ folders: Folder[] }>(`/api/mail/folders${opts.fresh ? '?fresh=1' : ''}`, { background: opts.background ?? false });
  mailStore.patch({ folders: r.folders, foldersLoadedAt: Date.now() });
  return r.folders;
}

export async function loadList(k: ListKey, opts: { more?: boolean; force?: boolean; background?: boolean } = {}): Promise<void> {
  const key = listKey(k);
  const cur = mailStore.get().lists[key] ?? emptyList();
  if (cur.loading) return;
  if (!opts.more && !opts.force && !cur.stale && cur.loadedAt && Date.now() - cur.loadedAt < 30_000) return;
  if (k.view === 'search' && !k.q.trim()) {
    setList(key, { items: [], more: false, loading: false, error: null, loadedAt: Date.now(), stale: false });
    return;
  }
  setList(key, { loading: true, error: null });
  const round = staleRound;
  try {
    const query: Partial<MailListQuery> = { view: k.view, folder: k.folder, sort: k.sort, processed: k.processed, limit: PAGE, skip: opts.more ? cur.items.length : 0 };
    if (k.q.trim()) query.q = k.q.trim();
    const r = await api<MailListResponse>(`/api/mail${qs(query)}`, { background: opts.background ?? false });
    // Een antwoord dat een eigen actie van zojuist nog niet kent draait die niet terug: weg is weg, vlag en gelezen blijven zoals gezet.
    const fresh = r.items.flatMap((i) => {
      const own = ownRecent(i.id, OWN_LIST_MS);
      if (!own) return [i];
      return own === 'archive' || own === 'delete' || own === 'move' || viewLeftBy(own) === k.view ? [] : [{ ...i, ...fieldsAfter(own) }];
    });
    // De lijst zoals hij nu is (tijdens het laden kan een actie items weggehaald hebben); bij verversen blijven de items
    // voorbij de eerste pagina staan (ontdubbeld), zodat een lijst die al verder geladen was niet inkrimpt.
    const now = mailStore.get().lists[key] ?? cur;
    const items = opts.more ? dedupe([...now.items, ...fresh], k.view) : r.more ? dedupe([...fresh, ...now.items.slice(r.items.length)], k.view) : fresh;
    setList(key, { items, more: r.more, loading: false, loadedAt: Date.now(), stale: (staleAt.get(key) ?? 0) > round });
  } catch (e) {
    // Ook `stale` uit: anders probeert het scherm het meteen opnieuw, en opnieuw. De minuutverversing pakt het later op.
    setList(key, { loading: false, error: (e as Error).message, stale: false });
  }
}

function dedupe(items: MailListItem[], view: MailView): MailListItem[] {
  const seen = new Set<string>();
  return items.filter((i) => {
    const k = itemKey(i, view);
    return seen.has(k) ? false : (seen.add(k), true);
  });
}

/**
 * Wat de lijst laat zien: bij Gevlagd/Ongelezen verdwijnen mails met een lopende taak (die staan bij Verwerkt), en
 * mails waarvan de app inmiddels weet dat de vlag weg is of dat ze gelezen zijn (bijvoorbeeld in Outlook gedaan).
 */
export function visibleItems(items: MailListItem[], view: MailView): MailListItem[] {
  if (view !== 'flagged' && view !== 'unread') return items;
  return items.filter((i) => !(i.task && HIDING_STATUSES.has(i.task.status)) && (view === 'flagged' ? i.flagged : !i.isRead));
}

/** De lijst van de kolom Gevlagd (ticket 30961): alle gevlagde mail, nieuwste eerst, zoals Gevlagd op het tabblad Mail met de
 * standaardsortering; dezelfde sleutel, dus met de standaardsortering delen de kolom en het tabblad één lijst. */
export const FLAGGED_LIST: ListKey = { view: 'flagged', folder: 'all', sort: 'newest', processed: 'all', q: '' };

/**
 * Wat de kolom Gevlagd laat zien: alle mails die gevlagd zijn, ook met een lopende of klare taak (die verbergt `visibleItems` op
 * het tabblad Mail, want daar staan ze bij Verwerkt of onder "Klaar voor jou"), nieuwste eerst. Een mail waarvan de app weet dat de
 * vlag weg is (in de lezer, of een eigen actie), valt weg.
 */
export function flaggedItems(items: MailListItem[]): MailListItem[] {
  const seen = new Set<string>();
  return items
    .filter((i) => i.flagged && !seen.has(i.id) && (seen.add(i.id), true))
    .map((item, index) => ({ item, index }))
    .sort((a, b) => (b.item.received ?? '').localeCompare(a.item.received ?? '') || a.index - b.index)
    .map((x) => x.item);
}

/** Een detail dat al onderweg is wordt gedeeld (kiezen, vooruit ophalen en verversen vragen soms dezelfde mail). */
const detailLoads = new Map<string, Promise<MailDetail>>();

export function loadDetail(id: string, opts: { force?: boolean; background?: boolean } = {}): Promise<MailDetail> {
  const cached = mailStore.get().details[id];
  if (cached && !opts.force && Date.now() - cached.at < 5 * 60_000) return Promise.resolve(cached.detail);
  const running = detailLoads.get(id);
  if (running) return running;
  const load = (async () => {
    try {
      const detail = await api<MailDetail>(`/api/mail/${encodeURIComponent(id)}`, { background: opts.background ?? false });
      mailStore.set((s) => {
        const errors = { ...s.detailErrors };
        delete errors[id];
        return { ...s, details: { ...s.details, [id]: { detail, at: Date.now() } }, detailErrors: errors };
      });
      // De lezer is verser dan de lijst: vlag en gelezen overnemen, zodat een in Outlook ontvlagde mail uit Gevlagd verdwijnt.
      updateItems((i) => (i.id === id && (i.flagged !== detail.flagged || i.isRead !== detail.isRead || i.flagStatus !== detail.flagStatus) ? { ...i, flagged: detail.flagged, flagStatus: detail.flagStatus, isRead: detail.isRead } : i));
      return detail;
    } catch (e) {
      mailStore.set((s) => ({ ...s, detailErrors: { ...s.detailErrors, [id]: (e as Error).message } }));
      throw e;
    } finally {
      detailLoads.delete(id);
    }
  })();
  detailLoads.set(id, load);
  return load;
}

/** Vooruit ophalen is achtergrondwerk: het wijkt bij de connector voor de mail die Jeroen echt aanklikt. Op de telefoon hooguit één. */
const prefetching = new Set<string>();
export function prefetch(ids: string[]): void {
  for (const id of app.get().narrow ? ids.slice(0, 1) : ids) {
    if (prefetching.has(id) || mailStore.get().details[id]) continue;
    prefetching.add(id);
    void loadDetail(id, { background: true })
      .catch(() => undefined)
      .finally(() => prefetching.delete(id));
  }
}

/** Volgende items na de selectie in de zichtbare lijst (voor Volgende en de selectie na Klaar). */
export function nextItems(items: MailListItem[], sel: Sel | string | null, count: number, view: MailView = 'flagged'): MailListItem[] {
  const idx = sel ? items.findIndex((i) => matchesSel(i, sel, view)) : -1;
  return items.slice(idx + 1, idx + 1 + count);
}

/** De eerste rij na de selectie die bij een andere mail hoort: de keuze na een actie, een instructie of Klaar (j/k en Volgende lopen rij voor rij). */
export function nextMail(items: MailListItem[], sel: Sel | string | null, view: MailView = 'flagged'): MailListItem | null {
  const id = typeof sel === 'string' ? sel : sel?.id;
  return nextItems(items, sel, items.length, view).find((i) => i.id !== id) ?? null;
}

/** Volgende mails na de selectie (voor vooruit ophalen). */
export function nextIds(items: MailListItem[], sel: Sel | string | null, count: number, view: MailView = 'flagged'): string[] {
  return nextItems(items, sel, count, view).map((i) => i.id);
}

/** Past `fn` toe op elke mail in elke lijst; `view` is de weergave van die lijst (null verwijdert de mail eruit). */
function updateItems(fn: (item: MailListItem, view: MailView) => MailListItem | null): void {
  mailStore.set((s) => {
    const lists: Record<string, ListState> = {};
    for (const [key, list] of Object.entries(s.lists)) {
      const view = key.split('|')[0] as MailView;
      const items = list.items.map((i) => fn(i, view)).filter((i): i is MailListItem => i !== null);
      lists[key] = items.length === list.items.length && items.every((it, i) => it === list.items[i]) ? list : { ...list, items };
    }
    return { ...s, lists };
  });
}

function updateDetail(id: string, patch: Partial<MailDetail>): void {
  mailStore.set((s) => {
    const d = s.details[id];
    if (!d) return s;
    return { ...s, details: { ...s.details, [id]: { detail: { ...d.detail, ...patch }, at: d.at } } };
  });
}

/**
 * Zet één mail terug zoals hij vóór een mislukte actie was: op zijn oude plek (na zijn oude buurman) in elke lijst waar hij
 * stond, met zijn oude velden, en in de lezer. Wat intussen elders veranderde (andere mails, andere lijsten) blijft staan.
 */
function restoreMail(id: string, before: MailState): void {
  mailStore.set((s) => {
    const lists: Record<string, ListState> = { ...s.lists };
    for (const [key, list] of Object.entries(s.lists)) {
      const old = before.lists[key];
      if (!old) continue;
      const view = key.split('|')[0] as MailView;
      let items = list.items;
      old.items.forEach((was, idx) => {
        if (was.id !== id) return;
        const k = itemKey(was, view);
        const cur = items.findIndex((i) => itemKey(i, view) === k);
        if (cur >= 0) {
          items = items.map((i, n) => (n === cur ? was : i));
          return;
        }
        const prev = idx > 0 ? itemKey(old.items[idx - 1]!, view) : null;
        const after = prev ? items.findIndex((i) => itemKey(i, view) === prev) : -1;
        const at = after >= 0 ? after + 1 : Math.min(idx, items.length);
        items = [...items.slice(0, at), was, ...items.slice(at)];
      });
      if (items !== list.items) lists[key] = { ...list, items };
    }
    const details = { ...s.details };
    const d = before.details[id];
    if (d) details[id] = d;
    else delete details[id];
    return { ...s, lists, details };
  });
}

/** Tellers direct meebewegen met een actie; de teller van de server overschrijft later. */
function bumpCounts(delta: Partial<Pick<Counts, 'flagged' | 'unread'>>): void {
  tasksStore.set((s) => ({ ...s, counts: { ...s.counts, flagged: Math.max(0, s.counts.flagged + (delta.flagged ?? 0)), unread: Math.max(0, s.counts.unread + (delta.unread ?? 0)) } }));
}

/** Welke lijstweergave een mail na deze actie niet meer hoort te tonen. */
export function viewLeftBy(action: MailAction): MailView | null {
  return action === 'unflag' ? 'flagged' : action === 'read' ? 'unread' : null;
}

/** Telt elke markStale, met per lijst de ronde van haar laatste markering: een lijst die tijdens het laden oud werd (een melding van de
 * stroom, 2.3.2), blijft na dat laden op stale, zodat het scherm hem nog een keer laadt; het antwoord dat al onderweg was, kent de
 * wijziging misschien nog niet. */
let staleRound = 0;
const staleAt = new Map<string, number>();

/** De map van een lijst uit haar sleutel (listKey: weergave, map, sortering, verwerkt, zoekterm). */
function folderOfKey(key: string): string {
  return key.split('|')[1] ?? '';
}

/** Alle lijsten op oud (en de mappen), of met `only` alleen de lijsten van die mappen (de mappen zelf dan niet). */
export function markStale(only?: (folder: string) => boolean): void {
  staleRound += 1;
  mailStore.set((s) => {
    const lists: Record<string, ListState> = {};
    for (const [key, list] of Object.entries(s.lists)) {
      const hit = !only || only(folderOfKey(key));
      if (hit) staleAt.set(key, staleRound);
      lists[key] = hit ? { ...list, stale: true } : list;
    }
    return only ? { ...s, lists } : { ...s, lists, foldersLoadedAt: 0 };
  });
}

/** De map Concepten (zoals de app haar kent) en de map uit de melding. */
function draftFolders(mapId: string | null | undefined): (folder: string) => boolean {
  const ids = new Set(mailStore.get().folders.filter((f) => f.wellKnown === 'drafts').map((f) => f.id));
  if (mapId) ids.add(mapId);
  return (folder) => ids.has(folder);
}

let folderReloadTimer: ReturnType<typeof setTimeout> | null = null;
let folderReloadLate: ReturnType<typeof setTimeout> | null = null;

/** Mappen (met tellers per map) opnieuw laden, kort na elkaar gebundeld; en nog eens later, want Outlook loopt soms even achter. */
export function scheduleFolderReload(): void {
  if (!folderReloadLate) {
    folderReloadLate = setTimeout(() => {
      folderReloadLate = null;
      void loadFolders(true, { background: true, fresh: true }).catch(() => undefined);
    }, 8000);
  }
  if (folderReloadTimer) return;
  folderReloadTimer = setTimeout(() => {
    folderReloadTimer = null;
    void loadFolders(true, { background: true, fresh: true }).catch(() => undefined);
  }, 1500);
}

/** Voor tests: geen herlading van de mappen meer in de wacht. */
export function forgetFolderReloads(): void {
  if (folderReloadTimer) clearTimeout(folderReloadTimer);
  if (folderReloadLate) clearTimeout(folderReloadLate);
  folderReloadTimer = null;
  folderReloadLate = null;
}

/** Alles verversen wat in Outlook veranderd kan zijn (achtergrondwerk): de huidige lijst, de mappen met tellers en de tellers bovenin. */
export async function refreshAll(k: ListKey): Promise<void> {
  await Promise.all([loadList(k, { force: true, background: true }), loadFolders(true, { background: true }).catch(() => undefined), loadCounts({ background: true }).catch(() => undefined)]);
}

export function forgetMail(id: string): void {
  scheduleFolderReload();
  updateItems((i) => (i.id === id ? null : i));
  mailStore.set((s) => {
    const details = { ...s.details };
    delete details[id];
    return { ...s, details };
  });
}

const ACTION_TEXT: Record<MailAction, string> = { archive: 'Gearchiveerd', delete: 'Verwijderd', move: 'Verplaatst', flag: 'Gevlagd', unflag: 'Vlag weg', read: 'Gelezen', unread: 'Ongelezen' };

/** De mail zoals de app hem nu kent (uit een lijst of de lezer), voor de tellers. */
function findMail(s: MailState, id: string): Pick<MailListItem, 'flagged' | 'isRead'> | null {
  for (const list of Object.values(s.lists)) {
    const m = list.items.find((i) => i.id === id);
    if (m) return m;
  }
  return s.details[id]?.detail ?? null;
}

function fieldsAfter(action: MailAction): Partial<MailListItem> {
  return action === 'flag' ? { flagged: true, flagStatus: 'flagged' } : action === 'unflag' ? { flagged: false, flagStatus: 'complete' } : action === 'read' ? { isRead: true } : action === 'unread' ? { isRead: false } : {};
}

/** `undo`: het terugdraaien van een eigen actie; daarna telt weer wat de server over de mail zegt. */
export async function act(id: string, action: MailAction, opts: { folderId?: string; folderName?: string; silent?: boolean; undo?: boolean } = {}): Promise<MailActionResult> {
  const removes = action === 'archive' || action === 'delete' || action === 'move';
  const leaves = viewLeftBy(action);
  const before = mailStore.get();
  const known = findMail(before, id);
  const wasFlagged = known?.flagged;
  const wasRead = known?.isRead;
  if (opts.undo) ownActions.delete(id);
  else noteOwn(id, action);
  // Optimistisch: uit de lijst (verplaatsen, of weg uit Gevlagd/Ongelezen), velden bijwerken, tellers meebewegen.
  if (removes) updateItems((i) => (i.id === id ? null : i));
  else updateItems((i, view) => (i.id !== id ? i : view === leaves ? null : { ...i, ...fieldsAfter(action) }));
  if (!removes) updateDetail(id, fieldsAfter(action));
  const delta = { flagged: action === 'flag' && wasFlagged === false ? 1 : action === 'unflag' && wasFlagged !== false ? -1 : 0, unread: action === 'read' && wasRead !== true ? -1 : action === 'unread' && wasRead !== false ? 1 : 0 };
  bumpCounts(delta);
  try {
    const r = await api<MailActionResult>(`/api/mail/${encodeURIComponent(id)}/actions`, { method: 'POST', body: { action, ...(opts.folderId ? { folderId: opts.folderId } : {}) } });
    // Het venster voor de eigen actie loopt vanaf het moment dat de connector klaar was.
    if (!opts.undo) noteOwn(id, action);
    if (!opts.silent) {
      const text = action === 'move' && opts.folderName ? `Verplaatst naar ${opts.folderName}` : ACTION_TEXT[action];
      const inverse: Record<string, MailAction> = { flag: 'unflag', unflag: 'flag', read: 'unread', unread: 'read' };
      const undo = removes && r.previousFolderId ? { label: 'Ongedaan maken', run: async () => void (await act(r.id, 'move', { folderId: r.previousFolderId!, silent: true, undo: true }).then(() => markStale())) } : inverse[action] ? { label: 'Ongedaan maken', run: async () => void (await act(id, inverse[action]!, { silent: true, undo: true }).then(() => markStale())) } : undefined;
      toast(text, { tone: 'ok', action: undo });
    }
    return r;
  } catch (e) {
    // Alleen deze mail terug (plek, velden, lezer) en de eigen tellerstap omkeren; de server zegt zo wat waar is.
    restoreMail(id, before);
    bumpCounts({ flagged: -delta.flagged, unread: -delta.unread });
    ownActions.delete(id);
    markStale();
    throw e;
  }
}

export interface EodResult {
  ok: true;
  day: string;
  task: Task;
}

/** Doorzetten naar de einde-dag-samenvatting: Jerome schrijft een conceptantwoord, de kennis komt om 18:00 in de samenvatting. */
export async function markEod(id: string, opts: { silent?: boolean } = {}): Promise<EodResult> {
  const before = mailStore.get();
  const day = isoDay(new Date());
  updateItems((i) => (i.id === id ? { ...i, eodDay: day } : i));
  updateDetail(id, { eodDay: day });
  try {
    const r = await api<EodResult>(`/api/mail/${encodeURIComponent(id)}/eod`, { method: 'POST', body: {} });
    if (r.day !== day) {
      updateItems((i) => (i.id === id ? { ...i, eodDay: r.day } : i));
      updateDetail(id, { eodDay: r.day });
    }
    if (r.task) {
      upsertTask(r.task);
      applyTaskToLists(r.task);
    }
    if (!opts.silent) toast('Naar einde dag; Jerome schrijft een conceptantwoord', { tone: 'ok', action: { label: 'Ongedaan maken', run: async () => void (await unmarkEod(id, { silent: true }).then(() => markStale())) } });
    return r;
  } catch (e) {
    restoreMail(id, before);
    markStale();
    throw e;
  }
}

export async function unmarkEod(id: string, opts: { silent?: boolean } = {}): Promise<void> {
  const before = mailStore.get();
  updateItems((i) => (i.id === id ? { ...i, eodDay: null } : i));
  updateDetail(id, { eodDay: null });
  try {
    await api(`/api/mail/${encodeURIComponent(id)}/eod/remove`, { method: 'POST', body: {} });
    if (!opts.silent) toast('Uit de einde-dag-samenvatting', { tone: 'ok' });
  } catch (e) {
    restoreMail(id, before);
    markStale();
    throw e;
  }
}

export function summaryOf(t: Task): TaskSummary {
  const split = t.artifacts['split'] as { index?: unknown; total?: unknown } | undefined;
  const part = split && typeof split.index === 'number' && typeof split.total === 'number' && split.total > 1 ? { index: split.index, total: split.total } : null;
  return { id: t.id, status: t.status, kind: t.kind, instruction: t.instruction, progress: t.progress, resultType: t.result?.type ?? null, updatedAt: t.updatedAt, split: part };
}

export async function instruct(id: string, body: InstructionBody): Promise<Task> {
  const task = await api<Task>(`/api/mail/${encodeURIComponent(id)}/instruction`, { method: 'POST', body });
  applyTaskToLists(task);
  return task;
}

/** Hoort een taak met deze status in dit Verwerkt-filter thuis? */
export function fitsProcessed(status: Task['status'], processed: ProcessedFilter): boolean {
  if (processed === 'all') return HIDING_STATUSES.has(status) || status === 'failed';
  if (processed === 'running') return status === 'running' || status === 'queued';
  return status === processed;
}

export function itemFromTask(task: Task, summary: TaskSummary): MailListItem {
  const snap = (task.artifacts['mail'] ?? {}) as Partial<MailListItem>;
  return { id: task.messageId, subject: task.subject, from: task.fromAddress ? { name: task.fromName ?? task.fromAddress, address: task.fromAddress } : null, to: snap.to ?? [], received: task.receivedAt, modified: task.receivedAt, preview: snap.preview ?? '', isRead: true, isDraft: false, flagged: snap.flagStatus === 'flagged', flagStatus: snap.flagStatus ?? 'notFlagged', flagDue: null, hasAttachments: snap.hasAttachments ?? false, importance: 'normal', webLink: null, conversationId: task.conversationId, parentFolderId: task.folderId, lastVerb: snap.lastVerb ?? null, lastVerbAt: snap.lastVerbAt ?? null, urgency: null, urgencyReason: null, task: summary };
}

/**
 * Werkt de taakinformatie bij in alle lijsten. Bij Verwerkt is elk item een taak: het item van deze taak wordt
 * bijgewerkt, verdwijnt als de taak niet meer in het filter past, of komt erbij (naast de andere taken van
 * dezelfde mail). In de andere lijsten draagt de mail de nieuwste actieve taak; is alles afgerond, dan is de
 * vlag weg en verdwijnt de mail uit Gevlagd en Ongelezen.
 */
export function applyTaskToLists(task: Task): void {
  upsertTask(task);
  const summary = summaryOf(task);
  const best = latestTaskForMessage(tasksStore.get(), task.messageId) ?? task;
  const bestSummary = best.id === task.id ? summary : summaryOf(best);
  mailStore.set((s) => {
    const lists: Record<string, ListState> = {};
    for (const [key, list] of Object.entries(s.lists)) {
      const [view, , , processed] = key.split('|') as [MailView, string, string, ProcessedFilter];
      let items = list.items;
      if (view === 'processed') {
        const fits = fitsProcessed(task.status, processed);
        const idx = list.items.findIndex((i) => i.task?.id === task.id);
        if (idx >= 0) items = fits ? list.items.map((i, n) => (n === idx ? { ...i, task: summary } : i)) : list.items.filter((_, n) => n !== idx);
        else if (fits && list.loadedAt) {
          let last = -1;
          list.items.forEach((i, n) => {
            if (i.id === task.messageId) last = n;
          });
          const fresh = itemFromTask(task, summary);
          items = last >= 0 ? [...list.items.slice(0, last + 1), fresh, ...list.items.slice(last + 1)] : [fresh, ...list.items];
        }
      } else {
        const gone = best.status === 'finished' && (view === 'flagged' || view === 'unread');
        items = list.items.flatMap((i) => (i.id !== task.messageId ? [i] : gone ? [] : [{ ...i, task: bestSummary }]));
      }
      lists[key] = items === list.items ? list : { ...list, items };
    }
    const details = { ...s.details };
    const d = details[task.messageId];
    if (d) details[task.messageId] = { detail: { ...d.detail, task: bestSummary }, at: d.at };
    return { ...s, lists, details };
  });
}

export function applyMailEvent(ev: WsEvent): void {
  if (ev.type === 'mail.changed') {
    // Een lege lijst (een inhaalslag van de stroom, 2.3.2): alles kan veranderd zijn, ook de open mail.
    const all = ev.ids.length === 0;
    // Eigen acties van zojuist: lijsten en lezer kloppen al (optimistisch); alleen de mappen met tellers verversen.
    const foreign = all ? Object.keys(mailStore.get().details) : ev.ids.filter((id) => !ownRecent(id, OWN_EVENT_MS));
    // Een concept (bewaren in Jerome of in Outlook, vaak elke paar seconden; de stroom noemt de map): alleen de lijsten van Concepten.
    // Bewaren verandert geen aantal (geen herlading van de mappen); een nieuw concept het aantal bij Concepten wel.
    const draft = ev.bekendeMap === 'drafts';
    if (foreign.length || all) {
      // Een bekend detail blijft staan (de lezer knippert niet leeg) maar geldt als oud; de open mail wordt stil ververst.
      mailStore.set((s) => {
        const details = { ...s.details };
        for (const id of foreign) if (details[id]) details[id] = { ...details[id]!, at: 0 };
        return { ...s, details };
      });
      for (const id of all ? [...watched.keys()] : foreign) if (watched.has(id)) void loadDetail(id, { force: true, background: true }).catch(() => undefined);
      markStale(draft ? draftFolders(ev.mapId) : undefined);
    }
    if (!(draft && ev.wijziging === 'updated')) scheduleFolderReload();
  } else if (ev.type === 'task.updated') {
    applyTaskToLists(ev.task);
    // Een afgeronde taak met een concept: het aantal concepten in de mappenbalk verandert.
    if (ev.task.status === 'done' && ev.task.result?.draftId) scheduleFolderReload();
  }
}

export async function scoreUrgency(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const r = await api<{ scores: Record<string, { urgency: number; reason: string }> }>('/api/mail/urgency', { method: 'POST', body: { ids } });
  updateItems((i) => (r.scores[i.id] ? { ...i, urgency: r.scores[i.id]!.urgency, urgencyReason: r.scores[i.id]!.reason } : i));
}
